class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.handleOverlayClick = this.close.bind(this);
    this.handleKeyUp = (evt) => evt.code === 'Escape' && this.close();

    this.addEventListener('keyup', this.handleKeyUp);

    this.overlay = this.querySelector('#CartDrawer-Overlay');
    if (this.overlay) {
      this.overlay.removeEventListener('click', this.handleOverlayClick);
      this.overlay.addEventListener('click', this.handleOverlayClick);
    }

    this.setHeaderCartIconAccessibility();

    this.addEventListener('change', (e) => {
      const upsellProductItem = e.target.closest('.ts-cart__upsell-product');
      if (!upsellProductItem) return;

      if (e.target.tagName !== 'SELECT') return;

      const option = e.target.options[e.target.selectedIndex];
      if (!option) return;

      const currentVariantInput = upsellProductItem.querySelector('input[name="current_variant"]');
      const comparePrice = upsellProductItem.querySelector('.ts-cart__upsell-price--compare');
      const mainPrice = upsellProductItem.querySelector('.ts-cart__upsell-price--main');
      const image = upsellProductItem.querySelector('.ts-cart__upsell-product-media img');

      if (currentVariantInput) currentVariantInput.value = option.value;
      if (comparePrice) comparePrice.innerHTML = option.dataset.compare || '';
      if (mainPrice) mainPrice.innerHTML = option.dataset.price || '';

      if (image && option.dataset.image) {
        image.src = option.dataset.image;
        image.srcset = `${option.dataset.image} 300w`;
      }
    });

    this.addEventListener('click', (e) => {
      const upsellButton = e.target.closest('[ts-upsell-add]');
      if (!upsellButton) return;

      const upsellProductItem = upsellButton.closest('.ts-cart__upsell-product');
      if (!upsellProductItem) return;

      const currentVariantInput = upsellProductItem.querySelector('input[name="current_variant"]');
      if (!currentVariantInput || !currentVariantInput.value) return;

      const upsellProduct = parseInt(currentVariantInput.value, 10);
      if (!upsellProduct) return;

      this.addUpsellItem(upsellProduct);
    });
  }

  addUpsellItem(upsellProduct) {
    const sections = this.getSectionsToRender().map((section) => section.id);
    const cartData = {
      items: [
        {
          id: upsellProduct,
          quantity: 1,
        },
      ],
      sections: sections,
      sections_url: window.location.pathname,
    };

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(cartData),
    })
      .then((response) => response.json())
      .then((response) => {
        if (!response.items) {
          throw new Error(response.description);
        }

        this.renderContents(response);
      })
      .catch((error) => {
        console.error(error);
      });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink || cartLink.dataset.drawerBound === 'true') return;

    cartLink.dataset.drawerBound = 'true';
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');

        if (containerToTrapFocusOn && focusElement) {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    listenCartChange();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling?.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner?.classList.contains('is-empty')) {
      drawerInner.classList.remove('is-empty');
    }

    this.productId = parsedState.id;

    const parsedSections = {};

    this.getSectionsToRender().forEach((section) => {
      if (!parsedState.sections[section.id]) return;

      if (!parsedSections[section.id]) {
        parsedSections[section.id] = new DOMParser().parseFromString(parsedState.sections[section.id], 'text/html');
      }

      const sectionDocument = parsedSections[section.id];
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      if (section.id === 'cart-drawer') {
        const newContent = sectionDocument.querySelector('#CartDrawer');
        if (!newContent) return;

        const newCartContent = newContent.querySelector('.ts-cart__content');
        const newDrawerFooter = newContent.querySelector('.drawer__footer');
        const newFree = newContent.querySelector('.ts-cart__free');
        const newFreeLabel = newContent.querySelector('.ts-cart__free-label');
        const newFreeLoad = newContent.querySelector('.ts-cart__free-load');
        const newFreeNotification = newContent.querySelector('.ts-cart__free-notification');

        const currentCartContent = this.querySelector('.ts-cart__content');
        const currentDrawerFooter = this.querySelector('.drawer__footer');
        const currentFree = this.querySelector('.ts-cart__free');
        const currentFreeLabel = this.querySelector('.ts-cart__free-label');
        const currentFreeLoad = this.querySelector('.ts-cart__free-load');
        const currentFreeNotification = this.querySelector('.ts-cart__free-notification');

        if (currentCartContent && newCartContent) {
          currentCartContent.innerHTML = newCartContent.innerHTML;
        }

        if (currentDrawerFooter && newDrawerFooter) {
          currentDrawerFooter.innerHTML = newDrawerFooter.innerHTML;
        }

        if (currentFree && currentFreeLabel && currentFreeLoad && currentFreeNotification && newFree && newFreeLabel && newFreeLoad && newFreeNotification) {
          currentFreeLabel.innerHTML = newFreeLabel.innerHTML;
          currentFreeLoad.style.width = `${newFreeLoad.dataset.diff}%`;

          if (currentFree.classList.value !== newFree.classList.value) {
            currentFree.classList.value = newFree.classList.value;
            currentFreeNotification.innerHTML = newFreeNotification.innerHTML;
            currentFreeNotification.classList.add('open');

            setTimeout(() => {
              currentFreeNotification.classList.remove('open');
            }, 2000);
          }
        }
      } else {
        const newSection = sectionDocument.querySelector(section.selector || '.shopify-section');
        if (newSection) {
          sectionElement.innerHTML = newSection.innerHTML;
        }
      }
    });

    this.overlay = this.querySelector('#CartDrawer-Overlay');
    if (this.overlay) {
      this.overlay.removeEventListener('click', this.handleOverlayClick);
      this.overlay.addEventListener('click', this.handleOverlayClick);
    }

    this.setHeaderCartIconAccessibility();
    this.open();
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);